import { useEffect, useRef, useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { If } from 'src/components/If';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  createDefaultAlertIndicatorConfigurations,
  getAllSharedWallets,
  getOwnerDefaultIndicatorsAlertConfigurations,
} from 'src/services/wallet';

import { Filters } from './Filters';
import { WalletCard } from './WalletCard';
import { useMonitorContext } from '../MonitorContextProvider';

export function SharedWalletsTab() {
  const { auth } = useAuthContext();
  const [searchName, setSearchName] = useState('');
  const [orderBy, setOrderBy] = useState('criticality:desc');
  const queryClient = useQueryClient();
  const hasTriedCreateRef = useRef<boolean>(false);
  const { defaultUserAlertConfigurations, setDefaultUserAlertConfigurations } =
    useMonitorContext();

  const {
    data: wallets,
    isLoading: isLoadingWallets,
    isFetching: isFetchingWallets,
  } = useQuery({
    queryKey: ['shared-wallets', searchName, orderBy],
    queryFn: () => getAllSharedWallets({ name: searchName, sort: [orderBy] }),
    onError: (error) => {
      toast.error('Erro ao buscar carteiras compartilhadas');
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const {
    data: defaultUserAlertConfigurationsData,
    isLoading: isLoadingDefaultUserAlertConfigurations,
  } = useQuery({
    queryKey: ['default-user-alert-configurations', auth?.user?.id],
    queryFn: () => getOwnerDefaultIndicatorsAlertConfigurations(),
    onSuccess: (data) => {
      setDefaultUserAlertConfigurations(data);
      if (data && data.length > 0) {
        hasTriedCreateRef.current = true;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { mutate: mutateCreateDefaultAlertIndicatorConfigurations } =
    useMutation({
      mutationFn: () => {
        if (defaultUserAlertConfigurations.length === 0) {
          return createDefaultAlertIndicatorConfigurations();
        } else {
          return Promise.resolve(defaultUserAlertConfigurations);
        }
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ['default-user-alert-configurations', auth?.user?.id],
        });
      },
      onError: () => {
        toast.error(
          'Erro ao criar configurações padrão de alertas de indicadores do usuário',
        );
        hasTriedCreateRef.current = false;
      },
    });

  const isLoadingOrFetchingWallets = isLoadingWallets || isFetchingWallets;

  useEffect(() => {
    if (hasTriedCreateRef.current) {
      return;
    }

    if (isLoadingDefaultUserAlertConfigurations) {
      return;
    }

    const hasConfigurations =
      (defaultUserAlertConfigurationsData &&
        defaultUserAlertConfigurationsData.length > 0) ||
      (defaultUserAlertConfigurations &&
        defaultUserAlertConfigurations.length > 0);

    if (hasConfigurations) {
      hasTriedCreateRef.current = true;
      return;
    }

    hasTriedCreateRef.current = true;
    mutateCreateDefaultAlertIndicatorConfigurations();
  }, [
    defaultUserAlertConfigurations,
    isLoadingDefaultUserAlertConfigurations,
    defaultUserAlertConfigurationsData,
    mutateCreateDefaultAlertIndicatorConfigurations,
  ]);

  return (
    <>
      <Filters
        title=""
        searchEntityName="wallet"
        onSearch={(value) => setSearchName(value)}
        onOrderBy={(value) => setOrderBy(value)}
        orderBy={orderBy}
      />

      <If condition={isLoadingOrFetchingWallets}>
        <Box
          width="100%"
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      </If>

      <If condition={!wallets?.data?.length && !isLoadingOrFetchingWallets}>
        <Box
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <Typography variant="h5" color="GrayText">
            Não encontramos carteiras
          </Typography>
        </Box>
      </If>

      <If condition={wallets?.data?.length && !isLoadingOrFetchingWallets}>
        <Box
          px={4}
          py={2}
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          flexWrap="wrap"
          alignItems="start"
          gap={1.5}>
          {wallets?.data?.map((wallet) => (
            <WalletCard
              key={wallet.id}
              id={wallet.id}
              name={wallet.name}
              ownerName={wallet.ownerName}
              fundsCount={wallet.fundsCount}
              quotasCount={wallet.quotasCount}
              criticality={wallet.criticality}
            />
          ))}
        </Box>
      </If>
    </>
  );
}
